import React, { useState, useEffect } from 'react';
import { 
    Database, RefreshCw, CheckCircle, AlertCircle, X, 
    Loader, TestTube, Save, Edit2, Trash2, 
    CheckCircle2, XCircle, AlertTriangle, MapPin, ChevronDown, ChevronUp
} from 'lucide-react';
import { apiClient } from '../../config/api';

const AdminVoiceBoxSyncPage = () => {
    const [databaseConfigs, setDatabaseConfigs] = useState([]);
    const [selectedConfigId, setSelectedConfigId] = useState('');
    const [loadingConfigs, setLoadingConfigs] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);

    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const [showMatchModal, setShowMatchModal] = useState(false);
    const [pendingMatches, setPendingMatches] = useState([]);
    const [selectedMatches, setSelectedMatches] = useState(new Set());
    const [unmatchedGroups, setUnmatchedGroups] = useState([]);
    const [unmatchedClients, setUnmatchedClients] = useState([]);

    const [mappings, setMappings] = useState([]);
    const [loadingMappings, setLoadingMappings] = useState(false);
    const [testingMapping, setTestingMapping] = useState(null);
    
    // Unsynced groups state
    const [unsyncedGroups, setUnsyncedGroups] = useState([]);
    const [showManualMappingModal, setShowManualMappingModal] = useState(false);
    const [selectedUnsyncedGroup, setSelectedUnsyncedGroup] = useState(null);
    const [availableVoiceBoxClients, setAvailableVoiceBoxClients] = useState([]);
    const [loadingVoiceBoxClients, setLoadingVoiceBoxClients] = useState(false);
    const [selectedVoiceBoxClient, setSelectedVoiceBoxClient] = useState(null);
    
    // Collapsible sections state
    const [mappingsExpanded, setMappingsExpanded] = useState(true);
    const [unsyncedExpanded, setUnsyncedExpanded] = useState(true);

    // Load database configs on mount
    useEffect(() => {
        loadDatabaseConfigs();
        loadMappings();
        loadUnsyncedGroups();
    }, []);

    // Update selected config when ID changes
    useEffect(() => {
        if (selectedConfigId) {
            const config = databaseConfigs.find(c => c.id.toString() === selectedConfigId);
            setSelectedConfig(config || null);
        } else {
            setSelectedConfig(null);
        }
    }, [selectedConfigId, databaseConfigs]);

    const loadDatabaseConfigs = async () => {
        setLoadingConfigs(true);
        try {
            const result = await apiClient.get('/admin/database/configurations');
            if (result.success && result.configurations) {
                // Filter to only SQL Server configs
                const sqlServerConfigs = result.configurations.filter(
                    config => config.type === 'sqlserver' || config.type === 'mssql'
                );
                setDatabaseConfigs(sqlServerConfigs);
                console.log(`Loaded ${sqlServerConfigs.length} SQL Server database configurations`);
            }
        } catch (error) {
            console.error('Failed to load database configurations:', error);
            alert('Failed to load database configurations: ' + (error.message || 'Unknown error'));
        } finally {
            setLoadingConfigs(false);
        }
    };

    const loadMappings = async () => {
        setLoadingMappings(true);
        try {
            const result = await apiClient.get('/voicebox/client-mappings');
            console.log('Loaded mappings result:', result);
            console.log('Mappings count:', result.mappings?.length || 0);
            setMappings(result.mappings || []);
            if (result.mappings && result.mappings.length === 0) {
                console.warn('No mappings found in response, but request succeeded');
            }
        } catch (error) {
            console.error('Failed to load mappings:', error);
            console.error('Error details:', error.message, error);
            alert('Failed to load mappings: ' + (error.message || 'Unknown error'));
        } finally {
            setLoadingMappings(false);
        }
    };

    const loadUnsyncedGroups = async () => {
        try {
            const result = await apiClient.get('/voicebox/unsynced-groups');
            setUnsyncedGroups(result.unsyncedGroups || []);
        } catch (error) {
            console.error('Failed to load unsynced groups:', error);
        }
    };

    const loadVoiceBoxClients = async () => {
        if (!selectedConfigId) {
            alert('Please select a database configuration first');
            return;
        }
        
        setLoadingVoiceBoxClients(true);
        try {
            const result = await apiClient.get(`/voicebox/voicebox-clients?database_config_id=${selectedConfigId}`);
            setAvailableVoiceBoxClients(result.clients || []);
        } catch (error) {
            console.error('Failed to load VoiceBox clients:', error);
            alert('Failed to load VoiceBox clients: ' + (error.message || 'Unknown error'));
        } finally {
            setLoadingVoiceBoxClients(false);
        }
    };

    const handleManualMapping = async () => {
        if (!selectedUnsyncedGroup || !selectedVoiceBoxClient || !selectedConfig) {
            alert('Please select both a group and a VoiceBox client');
            return;
        }
        
        try {
            const serverHost = selectedConfig.serverInstance || selectedConfig.host;
            const serverPort = selectedConfig.port || 1433;
            const username = selectedConfig.username;
            
            await apiClient.post('/voicebox/client-mappings', {
                group_id: selectedUnsyncedGroup.id,
                voicebox_client_id: selectedVoiceBoxClient.ClientID,
                voicebox_client_name: selectedVoiceBoxClient.Name,
                voicebox_database_name: selectedVoiceBoxClient.DatabaseName || selectedVoiceBoxClient.Database,
                voicebox_server_host: serverHost,
                voicebox_server_port: serverPort,
                voicebox_username: username,
                database_config_id: selectedConfigId,
                match_confidence: 'manual'
            });
            
            alert('Mapping created successfully!');
            setShowManualMappingModal(false);
            setSelectedUnsyncedGroup(null);
            setSelectedVoiceBoxClient(null);
            await loadMappings();
            await loadUnsyncedGroups();
        } catch (error) {
            console.error('Failed to create manual mapping:', error);
            alert('Failed to create mapping: ' + (error.message || 'Unknown error'));
        }
    };

    const syncClients = async () => {
        if (!selectedConfigId) {
            alert('Please select a database configuration first');
            return;
        }

        setSyncing(true);
        setSyncResult(null);
        try {
            const result = await apiClient.post('/voicebox/sync-clients', {
                database_config_id: selectedConfigId
            });
            
            // Show matches that need confirmation
            if (result.matches && result.matches.length > 0) {
                setPendingMatches(result.matches);
                // Pre-select all matches by default
                setSelectedMatches(new Set(result.matches.map((_, index) => index)));
                setUnmatchedGroups(result.unmatchedGroups || []);
                setUnmatchedClients(result.unmatchedClients || []);
                setShowMatchModal(true);
            } else {
                setSyncResult({
                    success: true,
                    message: `Found ${result.totalVoiceBoxClients} VoiceBox clients and ${result.totalGroups} groups. No matches found.`
                });
            }
        } catch (error) {
            console.error('Sync clients error:', error);
            const errorData = error.response?.data || {};
            
            // Check if it's a "client doesn't exist" error
            if (errorData.friendlyMessage || errorData.error === 'VoiceBox client database not found') {
                setSyncResult({ 
                    success: false, 
                    isClientNotCreated: true,
                    message: errorData.friendlyMessage || errorData.message || 'Client database not created yet',
                    details: errorData.details || error.message
                });
            } else {
                setSyncResult({ 
                    success: false, 
                    message: errorData.message || error.message || 'Sync failed. Check console for details.',
                    details: errorData.details
                });
            }
        } finally {
            setSyncing(false);
        }
    };

    const confirmMatches = async (matchesToConfirm) => {
        if (!selectedConfig) {
            alert('Database configuration is required');
            return;
        }

        try {
            // Use selected config details
            const serverHost = selectedConfig.serverInstance || selectedConfig.host;
            const serverPort = selectedConfig.port || 1433;
            const username = selectedConfig.username;

            let successCount = 0;
            let errorCount = 0;
            const errors = [];

            // Save each confirmed match
            for (const match of matchesToConfirm) {
                try {
                    console.log('Saving mapping:', match);
                    const result = await apiClient.post('/voicebox/client-mappings', {
                        group_id: match.group_id,
                        voicebox_client_id: match.voicebox_client_id,
                        voicebox_client_name: match.voicebox_client_name,
                        voicebox_database_name: match.voicebox_database_name,
                        voicebox_server_host: serverHost,
                        voicebox_server_port: serverPort,
                        voicebox_username: username,
                        // Password will be retrieved from database_config_id on backend
                        database_config_id: selectedConfigId,
                        match_confidence: match.match_confidence
                    });
                    console.log('Mapping saved successfully:', result);
                    successCount++;
                } catch (error) {
                    console.error(`Failed to save mapping for ${match.voicebox_client_name}:`, error);
                    errorCount++;
                    errors.push(`${match.voicebox_client_name}: ${error.message || 'Unknown error'}`);
                }
            }

            setShowMatchModal(false);
            setPendingMatches([]);
            setSelectedMatches(new Set());
            
            // Reload mappings and unsynced groups to show the saved ones
            await loadMappings();
            await loadUnsyncedGroups();
            
            if (errorCount > 0) {
                alert(`${successCount} mapping(s) saved successfully, but ${errorCount} failed:\n${errors.join('\n')}`);
            } else {
                alert(`Successfully mapped ${successCount} client(s)!`);
            }
        } catch (error) {
            console.error('Error in confirmMatches:', error);
            alert('Error saving mappings: ' + (error.message || 'Unknown error'));
        }
    };

    const testMappingConnection = async (mappingId) => {
        setTestingMapping(mappingId);
        try {
            const result = await apiClient.post(`/voicebox/client-mappings/${mappingId}/test`);
            if (result.success) {
                alert('Connection test successful!');
                loadMappings(); // Reload to get updated sync status
            } else {
                alert('Connection test failed: ' + (result.error || 'Unknown error'));
            }
        } catch (error) {
            alert('Connection test failed: ' + (error.message || 'Unknown error'));
        } finally {
            setTestingMapping(null);
        }
    };

    const deleteMapping = async (mappingId) => {
        if (!window.confirm('Are you sure you want to delete this mapping?')) {
            return;
        }

        try {
            await apiClient.delete(`/voicebox/client-mappings/${mappingId}`);
            await loadMappings();
            await loadUnsyncedGroups();
            alert('Mapping deleted successfully!');
        } catch (error) {
            alert('Error deleting mapping: ' + (error.message || 'Unknown error'));
        }
    };

    const getConfidenceBadge = (confidence) => {
        const badges = {
            exact: { color: 'bg-green-100 text-green-800', icon: CheckCircle2, label: 'Exact Match' },
            high: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle, label: 'High Confidence' },
            medium: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle, label: 'Medium Confidence' },
            low: { color: 'bg-orange-100 text-orange-800', icon: AlertCircle, label: 'Low Confidence' },
            manual: { color: 'bg-gray-100 text-gray-800', icon: Edit2, label: 'Manual' }
        };

        const badge = badges[confidence] || badges.manual;
        const Icon = badge.icon;

        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                <Icon size={12} />
                {badge.label}
            </span>
        );
    };

    const getSyncStatusBadge = (status) => {
        const badges = {
            synced: { color: 'bg-green-100 text-green-800', icon: CheckCircle2, label: 'Synced' },
            pending: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Pending' },
            error: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Error' },
            conflict: { color: 'bg-orange-100 text-orange-800', icon: AlertTriangle, label: 'Conflict' }
        };

        const badge = badges[status] || badges.pending;
        const Icon = badge.icon;

        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                <Icon size={12} />
                {badge.label}
            </span>
        );
    };

    return (
        <div>
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <Database size={24} />
                    VoiceBox Client Sync
                </h1>
                <p className="text-gray-600">
                    Configure VoiceBox main database connection and sync clients with Onboarding App groups.
                </p>
            </div>

            {/* Database Configuration Selector */}
            <div className="bg-white rounded-lg shadow-sm border mb-6">
                <div className="p-6 border-b">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Database size={20} />
                        Select VoiceBox Database Configuration
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Choose a SQL Server database configuration from your saved configurations.
                        This will be used to connect to the VoiceBox main database (where Clients table is located).
                    </p>
                </div>

                <div className="p-6">
                    {loadingConfigs ? (
                        <div className="flex items-center gap-2 text-gray-600">
                            <Loader className="animate-spin" size={18} />
                            <span>Loading database configurations...</span>
                        </div>
                    ) : databaseConfigs.length === 0 ? (
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                            <p className="text-sm text-yellow-800">
                                No SQL Server database configurations found. Please create one on the{' '}
                                <a href="/admin/database-config" className="underline font-medium">Database Configuration</a> page first.
                            </p>
                        </div>
                    ) : (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Database Configuration *
                            </label>
                            <select
                                value={selectedConfigId}
                                onChange={(e) => setSelectedConfigId(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">-- Select a database configuration --</option>
                                {databaseConfigs.map((config) => (
                                    <option key={config.id} value={config.id}>
                                        {config.name} ({config.host || config.serverInstance}:{config.port}/{config.database})
                                    </option>
                                ))}
                            </select>
                            
                            {selectedConfig && (
                                <div className="mt-4 p-4 bg-gray-50 rounded-md">
                                    <h3 className="text-sm font-medium text-gray-700 mb-2">Selected Configuration:</h3>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div><span className="font-medium">Name:</span> {selectedConfig.name}</div>
                                        <div><span className="font-medium">Host:</span> {selectedConfig.serverInstance || selectedConfig.host}</div>
                                        <div><span className="font-medium">Port:</span> {selectedConfig.port}</div>
                                        <div><span className="font-medium">Database:</span> {selectedConfig.database}</div>
                                        <div><span className="font-medium">Username:</span> {selectedConfig.username}</div>
                                        <div><span className="font-medium">Trust Certificate:</span> {selectedConfig.trustServerCertificate ? 'Yes' : 'No'}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Sync Clients Section */}
            {selectedConfigId && (
                <div className="bg-white rounded-lg shadow-sm border mb-6">
                    <div className="p-6 border-b">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <RefreshCw size={20} />
                            Sync Clients
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Match VoiceBox clients with Onboarding App groups. Review and confirm matches.
                        </p>
                    </div>

                    <div className="p-6">
                        {syncResult && (
                            <div className={`mb-4 p-4 rounded-md ${
                                syncResult.success 
                                    ? 'bg-green-50 text-green-800' 
                                    : syncResult.isClientNotCreated
                                    ? 'bg-blue-50 text-blue-800 border border-blue-200'
                                    : 'bg-red-50 text-red-800'
                            }`}>
                                <div className="flex items-start gap-2">
                                    {syncResult.success ? (
                                        <CheckCircle size={20} className="mt-0.5 flex-shrink-0" />
                                    ) : syncResult.isClientNotCreated ? (
                                        <AlertTriangle size={20} className="mt-0.5 flex-shrink-0" />
                                    ) : (
                                        <AlertCircle size={20} className="mt-0.5 flex-shrink-0" />
                                    )}
                                    <div className="flex-1">
                                        <p className="font-medium mb-1">{syncResult.message}</p>
                                        {syncResult.isClientNotCreated && (
                                            <div className="mt-2 text-sm">
                                                <p className="mb-2">This is normal during the onboarding process. Here's what to do:</p>
                                                <ol className="list-decimal list-inside space-y-1 ml-2">
                                                    <li>Create the client database in VoiceBox first</li>
                                                    <li>Ensure the database is accessible with the configured connection</li>
                                                    <li>Return here and sync again once the client exists</li>
                                                </ol>
                                                <p className="mt-2 text-xs italic">
                                                    You can still create messages locally in the meantime using the Dealership Onboarding page.
                                                </p>
                                            </div>
                                        )}
                                        {syncResult.details && (
                                            <p className="mt-2 text-xs opacity-75">{syncResult.details}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <button
                            onClick={syncClients}
                            disabled={syncing}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            {syncing ? (
                                <>
                                    <Loader className="animate-spin" size={18} />
                                    Syncing...
                                </>
                            ) : (
                                <>
                                    <RefreshCw size={18} />
                                    Sync Clients
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Client Mappings List */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div 
                    className="p-6 border-b cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setMappingsExpanded(!mappingsExpanded)}
                >
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Database size={20} />
                            Client Mappings ({mappings.length})
                        </div>
                        {mappingsExpanded ? (
                            <ChevronUp size={20} className="text-gray-400" />
                        ) : (
                            <ChevronDown size={20} className="text-gray-400" />
                        )}
                    </h2>
                </div>

                {mappingsExpanded && (
                    <>
                        {loadingMappings ? (
                            <div className="p-6 text-center">
                                <Loader className="animate-spin mx-auto" size={24} />
                                <p className="text-gray-600 mt-2">Loading mappings...</p>
                            </div>
                        ) : mappings.length === 0 ? (
                            <div className="p-6 text-center text-gray-500">
                                No client mappings found. Configure the main database and sync clients to get started.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">VoiceBox Client</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Database</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Match Confidence</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sync Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {mappings.map((mapping) => (
                                            <tr key={mapping.id} className={mapping.sync_status === 'synced' ? 'bg-green-50' : ''}>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        {mapping.sync_status === 'synced' && (
                                                            <CheckCircle2 size={16} className="text-green-600" />
                                                        )}
                                                        <div className="text-sm font-medium text-gray-900">{mapping.group_name}</div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">{mapping.voicebox_client_name}</div>
                                                    <div className="text-xs text-gray-500">ID: {mapping.voicebox_client_id}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">{mapping.voicebox_database_name}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {getConfidenceBadge(mapping.match_confidence)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {getSyncStatusBadge(mapping.sync_status)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                testMappingConnection(mapping.id);
                                                            }}
                                                            disabled={testingMapping === mapping.id}
                                                            className="text-blue-600 hover:text-blue-900 disabled:text-gray-400"
                                                            title="Test Connection"
                                                        >
                                                            {testingMapping === mapping.id ? (
                                                                <Loader className="animate-spin" size={16} />
                                                            ) : (
                                                                <TestTube size={16} />
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                deleteMapping(mapping.id);
                                                            }}
                                                            className="text-red-600 hover:text-red-900"
                                                            title="Delete Mapping"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Unsynced Groups Section */}
            <div className="bg-white rounded-lg shadow-sm border mt-6">
                <div 
                    className="p-6 border-b cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setUnsyncedExpanded(!unsyncedExpanded)}
                >
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <AlertCircle size={20} />
                            Unsynced Dealerships ({unsyncedGroups.length})
                        </div>
                        {unsyncedExpanded ? (
                            <ChevronUp size={20} className="text-gray-400" />
                        ) : (
                            <ChevronDown size={20} className="text-gray-400" />
                        )}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                        These dealerships don't have a VoiceBox mapping. You can manually map them to VoiceBox clients.
                    </p>
                </div>
                
                {unsyncedExpanded && (
                    <>
                        {unsyncedGroups.length === 0 ? (
                            <div className="p-6 text-center text-gray-500">
                                All active dealerships are synced with VoiceBox.
                            </div>
                        ) : (
                            <div className="p-6">
                                <div className="space-y-2">
                                    {unsyncedGroups.map(group => (
                                        <div key={group.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                                            <span className="text-sm font-medium text-gray-900">{group.name}</span>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedUnsyncedGroup(group);
                                                    setShowManualMappingModal(true);
                                                    loadVoiceBoxClients();
                                                }}
                                                className="px-3 py-1 text-sm text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 flex items-center gap-1"
                                            >
                                                <MapPin size={14} />
                                                Map to VoiceBox
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Match Confirmation Modal */}
            {showMatchModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b flex justify-between items-center">
                            <h2 className="text-xl font-semibold text-gray-900">
                                Confirm Client Mappings
                            </h2>
                            <button
                                onClick={() => setShowMatchModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-6">
                            <p className="text-sm text-gray-600 mb-4">
                                Review the suggested matches below. Select the ones you want to confirm.
                            </p>

                            {/* Matches */}
                            {pendingMatches.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                                        Suggested Matches ({pendingMatches.length})
                                    </h3>
                                    <div className="space-y-3">
                                        {pendingMatches.map((match, index) => (
                                            <div key={index} className="border rounded-lg p-4">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <span className="font-medium text-gray-900">{match.group_name}</span>
                                                            <span className="text-gray-400">→</span>
                                                            <span className="font-medium text-gray-900">{match.voicebox_client_name}</span>
                                                            {getConfidenceBadge(match.match_confidence)}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            Database: {match.voicebox_database_name}
                                                        </div>
                                                    </div>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedMatches.has(index)}
                                                        onChange={(e) => {
                                                            const newSelected = new Set(selectedMatches);
                                                            if (e.target.checked) {
                                                                newSelected.add(index);
                                                            } else {
                                                                newSelected.delete(index);
                                                            }
                                                            setSelectedMatches(newSelected);
                                                        }}
                                                        className="ml-4"
                                                        id={`match-${index}`}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Unmatched Groups */}
                            {unmatchedGroups.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                                        Unmatched Groups ({unmatchedGroups.length})
                                    </h3>
                                    <div className="text-sm text-gray-600">
                                        These groups don't have a matching VoiceBox client. You can map them manually later.
                                    </div>
                                    <div className="mt-2 space-y-1">
                                        {unmatchedGroups.map((group, index) => (
                                            <div key={index} className="text-sm text-gray-500">
                                                • {group.name}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Unmatched Clients */}
                            {unmatchedClients.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                                        Unmatched VoiceBox Clients ({unmatchedClients.length})
                                    </h3>
                                    <div className="text-sm text-gray-600">
                                        These VoiceBox clients don't have a matching group. You can map them manually later.
                                    </div>
                                    <div className="mt-2 space-y-1">
                                        {unmatchedClients.map((client, index) => (
                                            <div key={index} className="text-sm text-gray-500">
                                                • {client.Name} (ID: {client.ClientID})
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    onClick={() => setShowMatchModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        const confirmed = pendingMatches.filter((_, index) => 
                                            selectedMatches.has(index)
                                        );
                                        confirmMatches(confirmed);
                                    }}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Confirm Selected ({selectedMatches.size})
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Manual Mapping Modal */}
            {showManualMappingModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b flex justify-between items-center">
                            <h2 className="text-xl font-semibold text-gray-900">
                                Manually Map Dealership to VoiceBox Client
                            </h2>
                            <button
                                onClick={() => {
                                    setShowManualMappingModal(false);
                                    setSelectedUnsyncedGroup(null);
                                    setSelectedVoiceBoxClient(null);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Dealership
                                </label>
                                <div className="p-3 bg-gray-50 rounded-md text-sm text-gray-900">
                                    {selectedUnsyncedGroup?.name}
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    VoiceBox Client *
                                </label>
                                {loadingVoiceBoxClients ? (
                                    <div className="flex items-center gap-2 text-gray-600">
                                        <Loader className="animate-spin" size={18} />
                                        <span>Loading VoiceBox clients...</span>
                                    </div>
                                ) : availableVoiceBoxClients.length === 0 ? (
                                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
                                        No available VoiceBox clients found. All clients may already be mapped, or there was an error loading clients.
                                    </div>
                                ) : (
                                    <select
                                        value={selectedVoiceBoxClient?.ClientID || ''}
                                        onChange={(e) => {
                                            const client = availableVoiceBoxClients.find(
                                                c => c.ClientID.toString() === e.target.value
                                            );
                                            setSelectedVoiceBoxClient(client);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">-- Select a VoiceBox client --</option>
                                        {availableVoiceBoxClients.map(client => (
                                            <option key={client.ClientID} value={client.ClientID}>
                                                {client.Name} (ID: {client.ClientID}, DB: {client.DatabaseName || client.Database})
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    onClick={() => {
                                        setShowManualMappingModal(false);
                                        setSelectedUnsyncedGroup(null);
                                        setSelectedVoiceBoxClient(null);
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleManualMapping}
                                    disabled={!selectedVoiceBoxClient || loadingVoiceBoxClients}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                    Create Mapping
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminVoiceBoxSyncPage;

